import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { AdminPageLayout, FormDatePicker, FormInput, FormSelect, Section, Card, Grid, Col } from '../../components';
import { ErrorBanner } from '../../components/common';
import { FormActionGroup } from '../../components/admin/patterns';
import { generateNestedBreadcrumbs } from '../../utils/breadcrumbGenerator';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useToast } from '../../contexts/ToastContext';

const TeamForm = () => {
  const { programId, teamId, branch_id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const isEdit = !!teamId;

  const [loadingData, setLoadingData] = useState(false);
  const [program, setProgram] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [noProgramsAvailable, setNoProgramsAvailable] = useState(false);

  // Form validation hook
  const {
    formData,
    errors,
    touched,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFieldValue,
    submitError
  } = useFormValidation({
    initialValues: {
      program_id: '',
      name: '',
      age_group_label: '',
      target_age: '',
      min_birth_year: '',
      max_birth_year: '',
      age_group_description: '',
      season: '',
      max_capacity: '',
      start_date: '',
      end_date: '',
      registration_start: '',
      registration_end: '',
      schedule: {
        days: [],
        start_time: '',
        end_time: '',
        location: ''
      }
    },
    validationSchema: (values) => {
      const newErrors = {};

      // Required fields
      if (!values.name || values.name.trim() === '') {
        newErrors.name = 'Team name is required';
      }
      if (!values.age_group_label || values.age_group_label.trim() === '') {
        newErrors.age_group_label = 'Age group is required';
      }
      if (!values.season || values.season.trim() === '') {
        newErrors.season = 'Season is required';
      }
      if (!values.max_capacity) {
        newErrors.max_capacity = 'Team capacity is required';
      }
      
      // Program is required when at branch level (creating team without programId in URL)
      if (!programId && branch_id && (!values.program_id || values.program_id.trim() === '')) {
        newErrors.program_id = 'Program is required';
      }

      // Date validations
      if (values.start_date && values.end_date) {
        if (new Date(values.start_date) >= new Date(values.end_date)) {
          newErrors.end_date = 'End date must be after start date';
        }
      }

      if (values.registration_start && values.registration_end) {
        if (new Date(values.registration_start) >= new Date(values.registration_end)) {
          newErrors.registration_end = 'Registration end date must be after start date';
        }
      }

      return newErrors;
    },
    onSubmit: async (values) => {
      try {
        // Clean up form data
        const submitData = {
          program_id: values.program_id || undefined,
          name: values.name.trim(),
          age_group_label: values.age_group_label || undefined,
          target_age: values.target_age ? parseInt(values.target_age) : undefined,
          min_birth_year: values.min_birth_year ? parseInt(values.min_birth_year) : undefined,
          max_birth_year: values.max_birth_year ? parseInt(values.max_birth_year) : undefined,
          age_group_description: values.age_group_description || undefined,
          season: values.season.trim(),
          max_capacity: parseInt(values.max_capacity),
          start_date: values.start_date ? `${values.start_date}T00:00:00` : undefined,
          end_date: values.end_date ? `${values.end_date}T23:59:59` : undefined,
          registration_start: values.registration_start ? `${values.registration_start}T00:00:00` : undefined,
          registration_end: values.registration_end ? `${values.registration_end}T23:59:59` : undefined,
          schedule: values.schedule
        };
        
        console.log('TeamForm Submit - formData:', values);
        console.log('TeamForm Submit - submitData:', submitData);

        if (isEdit) {
          await apiService.updateTeam(teamId, submitData);
          showSuccess('Team updated successfully');
        } else {
          // Use programId from URL param if available, otherwise use selected program_id from form
          const targetProgramId = programId || values.program_id;
          console.log('TeamForm Submit - targetProgramId:', targetProgramId);
          await apiService.createTeam(targetProgramId, submitData);
          showSuccess('Team created successfully');
        }

        // Navigate back to appropriate page
        if (branch_id) {
          navigate(`/branch/${branch_id}/teams`);
        } else if (programId) {
          navigate(`/admin/programs/${programId}/teams`);
        } else {
          navigate(`/admin/teams`);
        }
      } catch (err) {
        showError(err.message || `Failed to ${isEdit ? 'update' : 'create'} team`);

        // Parse Pydantic validation errors if present
        try {
          const errorArray = JSON.parse(err.message);
          if (Array.isArray(errorArray)) {
            const fieldMap = {
              'program_id': 'program_id',
              'name': 'name',
              'age_group_label': 'age_group_label',
              'season': 'season',
              'max_capacity': 'max_capacity',
              'target_age': 'target_age',
              'min_birth_year': 'min_birth_year',
              'max_birth_year': 'max_birth_year'
            };

            errorArray.forEach(errorItem => {
              if (errorItem.loc && errorItem.loc.length > 0) {
                const fieldName = errorItem.loc[errorItem.loc.length - 1];
                if (fieldMap[fieldName]) {
                  setFieldValue(fieldName, formData[fieldName]);
                }
              }
            });
          }
        } catch (parseErr) {
          // Not a JSON error, just re-throw
        }
        throw err;
      }
    }
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load seasons - filter to current and future seasons only
        try {
          const response = await apiService.getSeasons({});
          const allSeasons = response.seasons || [];
          
          // Filter to current and future seasons only
          const today = new Date();
          const futureSeasons = allSeasons
            .filter(season => {
              const endDate = new Date(season.end_date);
              return endDate >= today;
            })
            .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
          
          setSeasons(futureSeasons);
        } catch (err) {
          console.error('Failed to load seasons:', err);
          setSeasons([]);
        }

        // Load program if viewing a specific program's teams
        if (programId) {
          const programData = await apiService.getProgram(programId);
          setProgram(programData);
        }
        // Load available programs if at branch level
        else if (branch_id) {
          // Fetch only active programs for team creation
          // Note: Backend allows both draft and active, but we only show active in UI
          const response = await apiService.getPrograms({
            program_status: ['active']
          });
          console.log('Programs API response:', response);
          const programsList = response.programs || response || [];
          console.log('Programs for team creation:', programsList);
          setPrograms(programsList);

          // Check if no programs are available for team creation
          if (programsList.length === 0) {
            setNoProgramsAvailable(true);
          } else {
            setNoProgramsAvailable(false);
          }
        }

        // Load team data if editing
        if (isEdit && teamId) {
          const team = await apiService.getTeam(teamId);

          // Set form data using hook setFieldValue
          setFieldValue('program_id', team.program_id || '');
          setFieldValue('name', team.name || '');
          setFieldValue('age_group_label', team.age_group_label || '');
          setFieldValue('target_age', team.target_age || '');
          setFieldValue('min_birth_year', team.min_birth_year || '');
          setFieldValue('max_birth_year', team.max_birth_year || '');
          setFieldValue('age_group_description', team.age_group_description || '');
          setFieldValue('season', team.season || '');
          setFieldValue('max_capacity', team.max_capacity || '');
          setFieldValue('start_date', team.start_date ? team.start_date.split('T')[0] : '');
          setFieldValue('end_date', team.end_date ? team.end_date.split('T')[0] : '');
          setFieldValue('registration_start', team.registration_start ? team.registration_start.split('T')[0] : '');
          setFieldValue('registration_end', team.registration_end ? team.registration_end.split('T')[0] : '');

          const schedule = team.schedule || {
            days: [],
            start_time: '',
            end_time: '',
            location: ''
          };
          setFieldValue('schedule', schedule);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoadingData(false);
      }
    };

    setLoadingData(true);
    loadData();
  }, [programId, teamId, branch_id]);

  const handleScheduleChange = (e) => {
    const { name, value } = e.target;
    const newSchedule = {
      ...formData.schedule,
      [name]: value
    };
    setFieldValue('schedule', newSchedule);
  };

  const handleDaysChange = (day) => {
    const days = formData.schedule.days || [];
    const newDays = days.includes(day)
      ? days.filter(d => d !== day)
      : [...days, day];

    const newSchedule = {
      ...formData.schedule,
      days: newDays
    };
    setFieldValue('schedule', newSchedule);
  };

  // Generate breadcrumbs with context
  let breadcrumbItems = null;
  
  if (branch_id) {
    // Branch-level team breadcrumbs
    breadcrumbItems = generateNestedBreadcrumbs(
      window.location.pathname,
      [
        { label: 'Branch Dashboard', path: `/branch/${branch_id}/dashboard` },
        { label: 'Teams', path: `/branch/${branch_id}/teams` },
        { label: isEdit ? 'Edit Team' : 'New Team', path: null }
      ]
    );
  } else if (program) {
    // Program-level team breadcrumbs
    breadcrumbItems = generateNestedBreadcrumbs(
      window.location.pathname,
      [
        { label: 'Programs', path: '/admin/programs' },
        { label: program.name, path: '/admin/programs' },
        { label: 'Teams', path: `/admin/programs/${programId}/teams` },
        { label: isEdit ? 'Edit Team' : 'New Team', path: null }
      ]
    );
  }

  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (loadingData && isEdit && !formData.name) {
    return (
      <AdminPageLayout
        title={isEdit ? 'EDIT TEAM' : 'NEW TEAM'}
        subtitle="Loading team information..."
        backPath={branch_id ? `/branch/${branch_id}/teams` : programId ? `/admin/programs/${programId}/teams` : `/admin/teams`}
        maxWidth="1400px"
      >
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          Loading team...
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title={isEdit ? 'EDIT TEAM' : 'NEW TEAM'}
      subtitle="Create or edit team details"
      backPath={branch_id ? `/branch/${branch_id}/teams` : programId ? `/admin/programs/${programId}/teams` : `/admin/teams`}
      breadcrumbs={breadcrumbItems}
      maxWidth="1400px"
    >
      {program && (
        <div style={{ marginBottom: '1.5rem', padding: '0.75rem 1rem', background: 'var(--surface-lvl1)', borderRadius: '6px', color: 'var(--color-text-secondary)' }}>
          <strong style={{ color: 'var(--color-text)' }}>Program:</strong> {program.name}
        </div>
      )}

      {/* Warning message when no programs are available for team creation */}
      {noProgramsAvailable && !isEdit && (
        <ErrorBanner
          type="warning"
          message="No programs are available for team creation. Please ensure there is at least one program in draft or active status before creating teams."
        />
      )}

      <form onSubmit={handleFormSubmit}>
        <Section title="BASIC INFORMATION">
          <Card level={1}>
            {branch_id && (
              <FormSelect
                label="Program"
                name="program_id"
                value={formData.program_id}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={errors.program_id || ''}
                placeholder="-- Select Program --"
                options={programs.map(prog => ({
                  value: prog.id,
                  label: prog.name
                }))}
                required
                disabled={isEdit}
                helpText={isEdit ? "Program cannot be changed after team creation" : ""}
              />
            )}

            <FormInput
              label="Team Name"
              name="name"
              value={formData.name}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.name && errors.name ? errors.name : ''}
              placeholder="e.g., 2015 (U10) Hawks"
              required
            />

            <Grid columns={2} gap="2rem">
              <Col>
                <FormSelect
                  label="Season"
                  name="season"
                  value={formData.season}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.season && errors.season ? errors.season : ''}
                  placeholder="-- Select Season --"
                  options={[
                    { value: '', label: '-- Select Season --' },
                    ...seasons.map(season => ({
                      value: season.name,
                      label: season.name
                    }))
                  ]}
                  required
                />
              </Col>
              <Col>
                <FormInput
                  type="number"
                  label="Team Capacity"
                  name="max_capacity"
                  value={formData.max_capacity}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.max_capacity && errors.max_capacity ? errors.max_capacity : ''}
                  min="1"
                  max="1000"
                  placeholder="20"
                  required
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="AGE GROUP">
          <Card level={1}>
            <FormInput
              label="Age Group Label"
              name="age_group_label"
              value={formData.age_group_label}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.age_group_label && errors.age_group_label ? errors.age_group_label : ''}
              placeholder="e.g., U10, U12"
              required
            />

            <Grid columns={3} gap="2rem">
              <Col>
                <FormInput
                  type="number"
                  label="Target Age"
                  name="target_age"
                  value={formData.target_age}
                  onChange={handleFieldChange}
                  min="4"
                  max="25"
                  placeholder="e.g., 10"
                />
              </Col>
              <Col>
                <FormInput
                  type="number"
                  label="Min Birth Year"
                  name="min_birth_year"
                  value={formData.min_birth_year}
                  onChange={handleFieldChange}
                  placeholder="e.g., 2014"
                />
              </Col>
              <Col>
                <FormInput
                  type="number"
                  label="Max Birth Year"
                  name="max_birth_year"
                  value={formData.max_birth_year}
                  onChange={handleFieldChange}
                  placeholder="e.g., 2015"
                />
              </Col>
            </Grid>

            <FormInput
              label="Age Group Description"
              name="age_group_description"
              value={formData.age_group_description}
              onChange={handleFieldChange}
              placeholder="e.g., Under 10"
            />
          </Card>
        </Section>

        <Section title="TEAM DATES">
          <Card level={1}>
            <Grid columns={2} gap="2rem">
              <Col>
                <FormDatePicker
                  label="Start Date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.start_date ? errors.start_date : ''}
                />
              </Col>
              <Col>
                <FormDatePicker
                  label="End Date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.end_date && errors.end_date ? errors.end_date : ''}
                />
              </Col>
            </Grid>

            <Grid columns={2} gap="2rem">
              <Col>
                <FormDatePicker
                  label="Registration Start"
                  name="registration_start"
                  value={formData.registration_start}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.registration_start && errors.registration_start ? errors.registration_start : ''}
                />
              </Col>
              <Col>
                <FormDatePicker
                  label="Registration End"
                  name="registration_end"
                  value={formData.registration_end}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.registration_end && errors.registration_end ? errors.registration_end : ''}
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="SCHEDULE">
          <Card level={1}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.75rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                Training Days
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {weekDays.map(day => (
                  <label key={day} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    userSelect: 'none',
                    background: formData.schedule.days?.includes(day) ? 'var(--color-info-background)' : 'transparent',
                    borderColor: formData.schedule.days?.includes(day) ? 'var(--color-info)' : 'var(--color-border)'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.schedule.days?.includes(day) || false}
                      onChange={() => handleDaysChange(day)}
                      style={{ margin: 0, cursor: 'pointer' }}
                    />
                    <span style={{
                      fontSize: '0.875rem',
                      color: formData.schedule.days?.includes(day) ? 'var(--color-info)' : 'var(--color-text-secondary)',
                      fontWeight: formData.schedule.days?.includes(day) ? '600' : '400'
                    }}>
                      {day}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <Grid columns={2} gap="2rem">
              <Col>
                <FormInput
                  type="time"
                  label="Start Time"
                  name="start_time"
                  value={formData.schedule.start_time}
                  onChange={handleScheduleChange}
                />
              </Col>
              <Col>
                <FormInput
                  type="time"
                  label="End Time"
                  name="end_time"
                  value={formData.schedule.end_time}
                  onChange={handleScheduleChange}
                />
              </Col>
            </Grid>

            <FormInput
              label="Location"
              name="location"
              value={formData.schedule.location}
              onChange={handleScheduleChange}
              placeholder="e.g., Field A, Main Stadium"
            />
          </Card>
        </Section>

        <FormActionGroup
          onCancel={() => {
            if (branch_id) {
              navigate(`/branch/${branch_id}/teams`);
            } else if (programId) {
              navigate(`/admin/programs/${programId}/teams`);
            } else {
              navigate(`/admin/teams`);
            }
          }}
          onSave={handleFormSubmit}
          saveLabel={isEdit ? 'UPDATE TEAM' : 'CREATE TEAM'}
          loading={isSubmitting}
          type="submit"
        />
      </form>
    </AdminPageLayout>
  );
};

export default TeamForm;
